
import { defineComponent, onBeforeMount, reactive, ref, watch } from 'vue';
import { CustomerStampCard, useStampCard } from '@/compositions/useStampCard';
import DialogQRCodeReader from '@/components/organisms/DialogQRCodeReader.vue';
import ComsbiSaasScheme from '@comsbi/common/src/libs/comsbi-saas-scheme';
import StampCardPageContents from '@/components/organisms/stamp_card/StampCardPageContents.vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { ToastProgrammatic as Toast } from 'buefy';
import { objectFromEntries } from '@/utils';
import { useGeolocation } from '@/compositions/useGeolocation';

export default defineComponent({
  name: 'PagesStampCard',

  components: { StampCardPageContents, DialogQRCodeReader },

  setup(_) {
    const { resolve, stamp } = useStampCard();
    const route = useRoute();
    const router = useRouter();

    const refDialogQRCodeReader = ref<InstanceType<typeof DialogQRCodeReader>>();

    const state = reactive<{ id: string; card: CustomerStampCard | null; gotStamp: boolean; loading: boolean }>({
      id: route.params.id,
      card: null,
      gotStamp: false,
      loading: false,
    });

    const _loadContents = async () => {
      state.card = (await resolve(state.id)).result || null;
    };

    const { getLatLng } = useGeolocation();

    const giveStamp = async (modelId: string, itemKey?: string) => {
      try {
        state.loading = true;

        // QRのIDが一致しない場合はエラー
        if (state.id != modelId) {
          throw { response: { status: 485 } };
        }

        // CS-8234 GPS
        const _setting: any = state.card?.stampCard.settings;
        let latLng: { lat: number | null; lng: number | null } = { lat: null, lng: null };
        try {
          if (_setting && _setting.gps?.enabled) {
            latLng = await getLatLng();
          }
        } catch (e) {
          // HACK: 位置情報取得失敗 catchでハンドリングするためにthrow
          throw { response: { status: 430 } };
        }
        const { lat, lng } = _setting && _setting.gps?.enabled ? latLng : { lat: null, lng: null };

        // 付与処理
        const response = await stamp(modelId, itemKey, lat, lng);

        // 完了
        await _loadContents();
        state.gotStamp = true;

        if (response.result.goal && response.result.goal?.isGoal !== false) {
          // ゴール時
          Toast.open({
            message: `Congratulations!!<br>特典をプレゼントします！<br>トーク画面をご確認ください!`,
            type: 'is-success',
            position: 'is-top',
            duration: 5000,
          });
        } else {
          // スタンプGET
          // Toast.open({
          //   message: 'スタンプGET!',
          //   type: 'is-success',
          //   position: 'is-top',
          // });
        }
      } catch (e) {
        const _e = e as { response: { status: number } };
        const status: number = _e.response?.status;
        const message =
          {
            430: 'エラーコード: 430<br/>位置情報を取得できませんでした。<br/>設定を確認してください。',
            431: 'エラーコード: 431<br/>現在の位置情報では取得できません。<br/>範囲内で再度お試しください。',
            480: 'このスタンプカードはすでにコンプリート済みです',
            481: 'このスタンプはすでに取得済みです',
            482: '本日のスタンプはすでに取得済みです',
            483: 'このスタンプカードは有効期限切れです',
            484: '現在の時刻は取得できません',
            485: '読み取ったQRコードは、このスタンプカードに対応していません',
          }[status] || 'スタンプ付与に失敗しました';
        const type =
          {
            430: 'is-danger',
            431: 'is-danger',
            480: 'is-warning',
            481: 'is-warning',
            482: 'is-warning',
            483: 'is-danger',
            484: 'is-danger',
            485: 'is-danger',
          }[status] || 'is-danger';

        Toast.open({
          message,
          type,
          position: 'is-top',
          duration: 5000,
        });
        // throw e;
      } finally {
        state.loading = false;
      }
    };

    const parseQRValue = async (value: string) => {
      if (!value) return;
      if (value.indexOf('cs:') !== 0) {
        // 無効なコード
        console.log('invalid value. value:' + value);

        Toast.open({
          message: 'QRコードの読み取りに失敗しました。',
          type: 'is-warning',
          position: 'is-top',
        });
        // CS-9391 QR読み取り失敗でthrow
        throw new Error('invalid value. value:' + value);
      }

      const [modelKey, modelId, itemKey] = ComsbiSaasScheme.parse(value);
      const [_, queryStr = ''] = value.split('?');
      const query = objectFromEntries(
        queryStr
          .split('&')
          .filter(v => Boolean(v))
          .map(v => v.split('='))
      );

      if (modelKey !== ComsbiSaasScheme.STAMP_CARD) {
        //
        Toast.open({
          message: 'スタンプカード用のQRコードを読み込んでください。',
          type: 'is-warning',
          position: 'is-top',
        });
        return;
      }

      await giveStamp(modelId, itemKey);
    };

    const startQRCodeReader = async () => {
      refDialogQRCodeReader.value?.open();
    };

    const reload = async () => {
      state.loading = true;
      await _loadContents();
      state.loading = false;
    };

    onBeforeMount(async () => {
      await _loadContents();
      // CS-8177
      if (route.query.stamp) {
        const itemKey = (route.query.key as string) ?? undefined;
        await giveStamp(state.id, itemKey);
      }
    });

    watch(
      () => route,
      async (to, from) => {
        // パス更新
        console.log(to.params.id);
        state.id = to.params.id;
        await _loadContents();
      }
    );

    return {
      refDialogQRCodeReader,
      state,

      startQRCodeReader,
      parseQRValue,

      reload,

      // debug
      router: { ...router },
    };
  },
});
